<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <h1 class="headline">Business Models</h1>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" fab dark color="primary" @click="onCreateNew">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Create New Business Model</span>
      </v-tooltip>
    </v-row>
    <v-row>
      <v-col class="pa-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
          :search="search"
          item-key="id"
          class="elevation-1"
          v-bind="pagingOptions"
          mobile-break-point="1264"
        >
          <template v-slot:item.createPI="{ item }">
            <v-simple-checkbox
              v-model="item.createPI"
              :disabled="true"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.commission="{ item }">
            <v-simple-checkbox
              v-model="item.commission"
              :disabled="true"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.markup="{ item }">
            <v-simple-checkbox
              v-model="item.markup"
              :disabled="true"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.setAsOptional="{ item }">
            <v-simple-checkbox
              v-model="item.setAsOptional"
              :disabled="true"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.includesFlagOn="{ item }">
            <v-simple-checkbox
              v-model="item.includesFlagOn"
              :disabled="true"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.editableFlags="{ item }">
            <v-simple-checkbox
              v-model="item.editableFlags"
              :disabled="true"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              hide-view
              @deleteItem="onDeleteItem"
              @editItem="onEditItem"
            >
              <template v-slot:extraButton>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="mx-0"
                      @click="editItemContents(item)"
                    >
                      <v-icon color="blue" small>mdi-camera</v-icon>
                    </v-btn>
                  </template>
                  <span>Business Model Contents</span>
                </v-tooltip>
              </template>
            </default-list-actions>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <edit-business-model-dialog
      :show="showBusinessModelEditor"
      @close="showBusinessModelEditor = false"
      v-model="editedBusinessModel"
      @input="onSave"
    ></edit-business-model-dialog>
    <v-dialog
      v-if="editedBusinessModel && editedBusinessModel.id"
      v-model="showContentsDialog"
      min-width="1200"
      persistent
      :key="editedBusinessModel.id"
      scrollable
    >
      <v-card>
        <v-card-title class="grey lighten-3">
          <v-row>
            <v-icon class="draggable">mdi-drag</v-icon>
            <v-col class="py-0"
              >{{ editedBusinessModel.name }} - Contents</v-col
            >
          </v-row>
        </v-card-title>
        <v-card-text>
          <contents
            :owner-id="editedBusinessModel.id"
            owner-name="BUSINESS_MODEL"
          ></contents>
        </v-card-text>
        <v-card-actions class="grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn color="warning" text @click="showContentsDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div v-if="debug">
      <code>{{ businessModels }}</code>
    </div>
  </v-container>
</template>

<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import { removeGQLTypename } from "../../../../helpers";
import DefaultListActions from "../../shared/DefaultListActions";
import EditBusinessModelDialog from "./EditBusinessModelDialog";
import Contents from "../../shared/contents/Contents";
import gql from "graphql-tag";

const LIST_QUERY = gql`
  query businessModelsList {
    businessModels {
      edges {
        node {
          id
          name
          code
          description
          commission
          zeroSellPrice
          setAsOptional
          includesFlagOn
          createPI
          markup
          editableFlags
        }
      }
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteBusinessModel($id: ID!) {
    deleteBusinessModel(id: $id) {
      id
    }
  }
`;
const CREATE_MUTATION = gql`
  mutation createBusinessModel($input: BusinessModelInput) {
    createBusinessModel(input: $input) {
      id
      name
      code
      description
      commission
      zeroSellPrice
      setAsOptional
      includesFlagOn
      createPI
      markup
      editableFlags
    }
  }
`;
const UPDATE_MUTATION = gql`
  mutation updateBusinessModel($id: ID!, $input: BusinessModelInput) {
    updateBusinessModel(id: $id, input: $input) {
      id
      name
      code
      description
      commission
      zeroSellPrice
      setAsOptional
      includesFlagOn
      createPI
      markup
      editableFlags
    }
  }
`;
export default {
  name: "business-model-list",

  mixins: [ListMixin],

  components: {
    DefaultListActions,
    EditBusinessModelDialog,
    Contents,
  },

  data() {
    return {
      debug: false,
      showContentsDialog: false,
      headers: [
        {
          text: "Name",
          value: "name",
          name: "name",
        },
        {
          text: "Code",
          value: "code",
          name: "code",
        },
        {
          text: "Receivable Commission %",
          value: "commission",
          name: "commission",
        },
        {
          text: "Use Markup",
          value: "markup",
          name: "markup",
        },
        {
          text: "Create PI",
          value: "createPI",
          name: "createPI",
        },
        {
          text: "Set as Optional",
          value: "setAsOptional",
          name: "setAsOptional",
        },
        {
          text: "Includes Flag On",
          value: "includesFlagOn",
          name: "includesFlagOn",
        },
        {
          text: "Editable Flags",
          value: "editableFlags",
          name: "editableFlags",
        },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
        },
      ],
      editedBusinessModel: null,
      showBusinessModelEditor: false,
    };
  },

  computed: {
    items() {
      return this.businessModels && this.businessModels.edges
        ? this.businessModels.edges.map(removeGQLTypename)
        : [];
    },
  },

  methods: {
    editItemContents(item) {
      this.editedBusinessModel = { ...item };
      this.showContentsDialog = true;
    },
    onCreateNew() {
      this.editedBusinessModel = { name: "New Business Model", code: "" };
      this.showBusinessModelEditor = true;
    },

    onEditItem(item) {
      this.editedBusinessModel = { ...item };
      this.showBusinessModelEditor = true;
    },
    async onSave(payload) {
      this.showBusinessModelEditor = false;
      const { id, ...input } = payload;
      const mutation = payload.id ? UPDATE_MUTATION : CREATE_MUTATION;
      try {
        const mutationRes = await this.$apollo.mutate({
          mutation: mutation,
          variables: { input: { ...input }, id },
          refetchQueries: [
            {
              query: LIST_QUERY,
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Business Model Saved.",
        });
      } catch (error) {
        console.log(error);
      }
    },
    async onDeleteItem(payload) {
      try {
        await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: {
            id: payload.id,
          },
          refetchQueries: [
            {
              query: LIST_QUERY,
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Business Model deleted",
        });
      } catch (error) {
        console.log(error);
      }
    },
  },

  apollo: {
    businessModels: {
      query: LIST_QUERY,
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/admin/department-types/BusinessModelList.vue